import {
  HAVALA_PL_DISPLAY,
  HAVALA_PL_LABEL,
  HAVALA_PL_PARTY_ID,
  HAVALA_PL_PARTY_NAME,
  normalizePlAccountInput,
} from './havalaPl';

export function getDefaultPlDisplayLabel(tenantName?: string): string {
  if (tenantName && tenantName.trim()) {
    return `P&L - ${tenantName.trim()}`;
  }
  return HAVALA_PL_DISPLAY;
}

export function isPlSettlementPartyId(partyId: number | null | undefined): boolean {
  return partyId === HAVALA_PL_PARTY_ID;
}

export function formatSettlementPartyDisplay(
  settlementPartyId?: number | null,
  settlementPartyCode?: string | null,
  settlementPartyName?: string | null,
  tenantName?: string,
): string {
  const code = settlementPartyCode?.toUpperCase();
  if (
    settlementPartyId === HAVALA_PL_PARTY_ID ||
    code === 'P&L' ||
    code === 'PL' ||
    (settlementPartyId == null && !settlementPartyCode)
  ) {
    const label = settlementPartyName?.trim() || tenantName?.trim() || HAVALA_PL_PARTY_NAME;
    return getDefaultPlDisplayLabel(label === HAVALA_PL_PARTY_NAME ? undefined : label);
  }
  if (settlementPartyCode) {
    return settlementPartyName
      ? `${settlementPartyCode} - ${settlementPartyName}`
      : settlementPartyCode;
  }
  return '—';
}

export function parseSettlementPartyFromApi(raw: Record<string, unknown>): {
  settlementPartyId?: number;
  settlementPartyCode?: string;
  settlementPartyName?: string;
} {
  const idRaw = raw.settlementPartyId ?? raw.SettlementPartyId;
  const settlementPartyId =
    idRaw !== undefined && idRaw !== null ? Number(idRaw) : undefined;

  const codeRaw = raw.settlementPartyCode ?? raw.SettlementPartyCode;
  const settlementPartyCode = codeRaw != null ? String(codeRaw) : undefined;

  const nameRaw = raw.settlementPartyName ?? raw.SettlementPartyName;
  const settlementPartyName = nameRaw != null ? String(nameRaw) : undefined;

  return {
    settlementPartyId: Number.isFinite(settlementPartyId) ? settlementPartyId : undefined,
    settlementPartyCode,
    settlementPartyName,
  };
}

export function resolvePlLookup(tenantName?: string): {
  isValid: true;
  partyId: number;
  partyCode: string;
  partyName: string;
  displayValue: string;
} {
  const displayValue = getDefaultPlDisplayLabel(tenantName);
  const partyName = tenantName?.trim() || HAVALA_PL_PARTY_NAME;
  return {
    isValid: true,
    partyId: HAVALA_PL_PARTY_ID,
    partyCode: HAVALA_PL_LABEL,
    partyName,
    displayValue,
  };
}

export function isPlPartyCodeInput(code: string): boolean {
  return normalizePlAccountInput(code) !== null;
}

export function isSettlementPartyMatch(
  party: { partyId: number; partyCode?: string },
  settlement: { settlementPartyId?: number | null; settlementPartyCode?: string | null },
): boolean {
  const { settlementPartyId, settlementPartyCode } = settlement;
  if (settlementPartyId !== undefined && settlementPartyId !== null) {
    if (settlementPartyId === HAVALA_PL_PARTY_ID) {
      return (
        party.partyId === HAVALA_PL_PARTY_ID ||
        party.partyCode?.toUpperCase() === 'P&L' ||
        party.partyCode?.toUpperCase() === 'PL'
      );
    }
    return party.partyId === settlementPartyId;
  }
  if (settlementPartyCode) {
    return party.partyCode?.toUpperCase() === settlementPartyCode.toUpperCase();
  }
  return false;
}

export function sortPartiesSettlementLast<T extends { partyId: number; partyCode?: string }>(
  parties: T[],
  settlement: { settlementPartyId?: number | null; settlementPartyCode?: string | null },
): T[] {
  const settlementParties = parties.filter((p) => isSettlementPartyMatch(p, settlement));
  const others = parties.filter((p) => !isSettlementPartyMatch(p, settlement));
  return [...others, ...settlementParties];
}

export function getEffectiveSettlementParty(
  report?: {
    settlementPartyId?: number;
    settlementPartyCode?: string;
    settlementPartyName?: string;
  } | null,
  gameDetails?: {
    settlementPartyId?: number;
    settlementPartyCode?: string;
    settlementPartyName?: string;
  } | null,
): {
  settlementPartyId: number;
  settlementPartyCode: string;
  settlementPartyName?: string;
} {
  return {
    settlementPartyId:
      report?.settlementPartyId ?? gameDetails?.settlementPartyId ?? HAVALA_PL_PARTY_ID,
    settlementPartyCode:
      report?.settlementPartyCode ?? gameDetails?.settlementPartyCode ?? HAVALA_PL_LABEL,
    settlementPartyName: report?.settlementPartyName ?? gameDetails?.settlementPartyName,
  };
}
